import type { UseFetchReturn } from '@vueuse/core'

import { createFetch } from '@vueuse/core'

import type { FetchClientOptions } from '@/services/api/types.ts'

interface ParsedErrorData {
  message?: string
  errors?: Record<string, string[]>
  [key: string]: unknown
}

export type FetchClientResult<T> = UseFetchReturn<T> &
  PromiseLike<UseFetchReturn<T>> & {
    readonly parsedErrorData: ParsedErrorData | null
  }

export const fetchClient = <T = unknown>(
  url: string | URL,
  options: RequestInit & FetchClientOptions = {}
): FetchClientResult<T> => {
  const {
    preventConcurrent: _preventConcurrent = true,
    token: _token,
    signal,
    responseType = 'json',
    ...fetchOptions
  } = options

  // Capture the caller's signal (from ApiClient's AbortController)
  // VueUse's createFetch overwrites fetchOptions.signal with its own internal
  // AbortController on every execute() call, so we must restore it in beforeFetch
  const callerSignal = signal

  // Store parsed error data in a ref-like object so we can mutate it
  const errorDataContainer: { value: ParsedErrorData | null } = { value: null }

  const customFetch = createFetch({
    options: {
      async beforeFetch({ options }) {
        // Restore the caller's abort signal — VueUse overwrites it with its own
        // internal AbortController.signal on every execute(), breaking external
        // cancellation via ApiClient's pendingRequests Map
        if (callerSignal) {
          options.signal = callerSignal
        }
        return { options }
      },
      async afterFetch({ data, response }) {
        // Only parse successful responses based on responseType
        // Error responses will be handled by onFetchError
        if (response.ok) {
          // For blob responses, return the response directly without consuming the body
          // The caller will handle calling response.blob()
          if (responseType === 'blob') {
            return {
              data: response, // Return raw response for blob handling
              response
            }
          }

          // 204 No Content (e.g. DELETE endpoints) has no body to parse.
          if (response.status === 204) {
            return {
              data: null,
              response
            }
          }

          // Default JSON parsing for JSON responses
          return {
            data: await response.json(),
            response
          }
        }
        // For error responses, don't consume the body - let onFetchError handle it
        return { data, response }
      },
      async onFetchError({ data, error, response }) {
        if (response && !response.ok) {
          try {
            const errorData = await response.json()

            // Store the parsed error data in the container so we can access it later
            errorDataContainer.value = errorData

            return {
              data: errorData,
              error
            }
          } catch (_e) {
            return { data, error }
          }
        }
        return { data, error }
      }
    }
  })

  const result = customFetch<T>(url.toString(), fetchOptions)

  // Attach a getter for the parsed error data to the result
  // This way it reads the current value from the container when accessed
  Object.defineProperty(result, 'parsedErrorData', {
    get() {
      return errorDataContainer.value
    }
  })

  return result as FetchClientResult<T>
}
